/**
 * useWatched — which drills this coach has already got through.
 *
 * Backed by public.drill_views (see the migration of the same date).
 * Presence of a row is the whole state: watched or not. There is no
 * partial progress here, because the only thing the UI does with it is
 * sink a card to the bottom of the grid.
 *
 * Optimistic on both sides. A coach who presses "watched" sees the card
 * move immediately and we reconcile after; if the write fails we put it
 * back, because silently doing nothing is worse than a card that jumps.
 *
 * Signed out, this is an empty set and every write is a no-op, so the
 * grid behaves exactly as it did before any of this existed.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "../../supabaseClient";

// Module-level so switching sections doesn't refetch the whole history
// on every page mount. Same five-minute window as useDrills.
const CACHE_MS = 5 * 60 * 1000;
let cache = { ids: null, ts: 0, userId: null };

export function useWatched() {
  const [ids, setIds] = useState(() => cache.ids || new Set());
  const [loading, setLoading] = useState(!cache.ids);
  const mounted = useRef(true);
  const userIdRef = useRef(cache.userId);

  useEffect(() => {
    mounted.current = true;
    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!mounted.current) return;
        if (!user) { userIdRef.current = null; setIds(new Set()); setLoading(false); return; }
        userIdRef.current = user.id;

        const fresh = cache.ids && cache.userId === user.id && Date.now() - cache.ts < CACHE_MS;
        if (fresh) { setIds(cache.ids); setLoading(false); return; }

        const { data, error } = await supabase
          .from("drill_views")
          .select("drill_id")
          .eq("user_id", user.id);
        if (!mounted.current) return;
        if (error) throw error;

        const next = new Set((data || []).map(r => r.drill_id));
        cache = { ids: next, ts: Date.now(), userId: user.id };
        setIds(next);
      } catch (e) {
        // A library that sorts the old way is a much smaller problem
        // than a library that doesn't render, so this failure is quiet.
        console.warn("[useWatched] fetch failed, treating everything as unwatched:", e.message);
      } finally {
        if (mounted.current) setLoading(false);
      }
    })();
    return () => { mounted.current = false; };
  }, []);

  const write = useCallback((drillId, next, pct, manual) => {
    const userId = userIdRef.current;
    if (!userId || !drillId) return;

    const before = cache.ids || new Set();
    const after = new Set(before);
    if (next) after.add(drillId); else after.delete(drillId);
    cache = { ...cache, ids: after, ts: Date.now(), userId };
    setIds(after);

    const revert = () => {
      cache = { ...cache, ids: before, ts: Date.now(), userId };
      if (mounted.current) setIds(before);
    };

    const q = next
      ? supabase.from("drill_views").upsert(
          { user_id: userId, drill_id: drillId, pct, manual, watched_at: new Date().toISOString() },
          { onConflict: "user_id,drill_id" })
      : supabase.from("drill_views").delete().eq("user_id", userId).eq("drill_id", drillId);

    Promise.resolve(q).then(({ error }) => { if (error) revert(); }).catch(revert);
  }, []);

  // Crossing the threshold during playback. Never un-watches, and never
  // rewrites a row that already exists: a coach rewatching a drill
  // shouldn't generate a write every time, and shouldn't have their
  // original manual mark overwritten by an automatic one.
  const markWatched = useCallback((drillId, pct = 50) => {
    if ((cache.ids || new Set()).has(drillId)) return;
    write(drillId, true, Math.round(pct), false);
  }, [write]);

  // The explicit control. Needs to work in both directions, because the
  // cards autoplay muted inline - a coach who clicks one and walks away
  // will be told they watched something they didn't.
  const toggleWatched = useCallback((drillId) => {
    const on = (cache.ids || new Set()).has(drillId);
    write(drillId, !on, on ? 0 : 100, true);
  }, [write]);

  return { watched: ids, loading, markWatched, toggleWatched };
}

export default useWatched;
